/**
 * @fileoverview CreateOptions Component - Initial choice for creating incident reports
 * Allows users to choose between creating from scratch or generating via AI.
 * 
 * @author SmarterServices Platform Team
 * @version 1.0.0
 * @since 2025-10-21
 */

import React from 'react';
import { View, Flex, Button, Heading, Text, IconEditLine, IconAiColoredSolid, IconAiSolid } from '@smarterservices/smarter-ui';
import { useHotkeys } from 'react-hotkeys-hook';
import NavigationalToolTip from '@ui/shared/components/Common/NavigationalToolTip';


/**
 * CreateOptions Component
 * 
 * Presents the user with options for creating a new incident report:
 * - Create from scratch (manual entry)
 * - Generate via AI (using annotations)
 * 
 * @component
 * @param {Object} props
 * @param {Function} props.onCreateFromScratch - Callback when user chooses to create manually
 * @param {Function} props.onGenerateWithAI - Callback when user chooses AI generation
 * @returns {JSX.Element} The rendered create options interface
 */
function CreateOptions({ onCreateFromScratch, onGenerateWithAI }) {

  const writeShortcut = 's';
  const generateShortcut = 'g';


  // Hotkey for "S" - Start from scratch
  useHotkeys(
    writeShortcut,
    (event) => {
      event.preventDefault();
      onCreateFromScratch();
    },
    {
      preventDefault: true,
      enableOnContentEditable: true,
      enableOnFormTags: true,
      description: 'Create from scratch'
    }
  );

  // Hotkey for "G" - Generate with AI
  useHotkeys(
    generateShortcut,
    (event) => {
      event.preventDefault();
      onGenerateWithAI();
    },
    {
      preventDefault: true,
      enableOnContentEditable: true,
      enableOnFormTags: true,
      description: 'Generate with AI'
    }
  );

  return (
    <View as="div" position="relative" padding="small" textAlign="center" width="95%" margin="none auto">
      <View as="div" margin="none none medium none">
        <Heading level="h3" margin="none none small none">
          Create Incident Report
        </Heading>
        <Text as="p" color="secondary" margin="none none small none">
          How would you like to create your incident report?
        </Text>
      </View>

      <Flex as="div" direction="row" gap="small" alignItems="stretch" width="100%">
        <Flex.Item shouldGrow shouldShrink width="50%">
          <View
            as="div"
            background="secondary"
            borderColor="primary"
            borderRadius="medium"
            borderWidth="small"
            padding="medium"
            textAlign="center"
            height="100%"
          >
            <IconEditLine size="medium" color="primary" />
            <Heading level="h4" margin="small none small none">
              Create from Scratch
            </Heading>
            <Text as="p" size="small" color="secondary" margin="none none medium none">
              Start with a blank form and write your report manually.
            </Text>
            <NavigationalToolTip
              description="Create from scratch"
              shortcut="s"
            >
              <Button
                color="primary"
                onClick={onCreateFromScratch}
                width="100%"
                margin="small none none none"
              >
                Start Writing
              </Button>
            </NavigationalToolTip>
          </View>
        </Flex.Item>

        <Flex.Item shouldGrow shouldShrink width="50%">
          <View
            as="div"
            background="secondary"
            borderColor="primary"
            borderRadius="medium"
            borderWidth="small"
            padding="medium"
            textAlign="center"
            height="100%"
          >
            <IconAiSolid size="medium" />
            <Heading level="h4" margin="small none small none">
              Generate with AI
            </Heading>
            <Text as="p" size="small" color="secondary" margin="none none medium none">
              Use flagged annotations to automatically generate a report draft.
            </Text>
            {/* TODO: Implement AI generation 
            <NavigationalToolTip
              description="Generate with AI"
              shortcut="g"
            >
              
              <Button
                color="ai-primary"
                renderIcon={IconAiSolid}
                onClick={onGenerateWithAI}
                width="100%"
                margin="small none none none"
              >
                Generate
              </Button>
              
            </NavigationalToolTip>
            */}
            <View as="div" margin="small none none none">
              <Text as="p" variant="contentImportant">
                Coming Soon!
              </Text>
            </View>
          </View>
        </Flex.Item>
      </Flex>
    </View>
  );
}

export default CreateOptions;
